import { useNotificationStore } from '../notifications/notificationStore.js';
import { Icon } from './Icon.js';

/**
 * The one-time ask, shown under the header until the user answers it.
 *
 * DELIBERATELY NOT AUTOMATIC: the app never calls `requestPermission()` on load.
 * A prompt raised without context is the pattern browsers now suppress and users
 * reflexively block, and a blocked origin cannot be un-blocked from script. So
 * this banner explains first and prompts only on a click.
 *
 * It appears only while the decision is genuinely open - a granted or denied
 * permission, or a dismissal, all hide it for good.
 */
export function NotificationPrompt() {
  const availability = useNotificationStore((state) => state.availability);
  const permission = useNotificationStore((state) => state.permission);
  const requesting = useNotificationStore((state) => state.requesting);
  const dismissed = useNotificationStore((state) => state.promptDismissed);
  const outcome = useNotificationStore((state) => state.lastOutcome);
  const enable = useNotificationStore((state) => state.enable);
  const dismissPrompt = useNotificationStore((state) => state.dismissPrompt);

  // Chrome's quieter messaging swaps the dialog for a bell icon in the address
  // bar, so the click appears to do nothing at all. Say what happened, and where
  // the prompt actually went.
  if (outcome === 'dismissed') {
    return (
      <div className="notice" role="status">
        <Icon name="bell" size={15} />
        <span>
          Your browser did not show the permission prompt. Look for a bell or blocked-notification
          icon at the right of the address bar and allow notifications there.
        </span>
        <button type="button" className="btn btn--ghost btn--sm" onClick={dismissPrompt}>
          Dismiss
        </button>
      </div>
    );
  }

  if (availability !== 'available' || permission !== 'default' || dismissed) {
    return null;
  }

  return (
    <div className="notice" role="status">
      <Icon name="bell" size={15} />
      <span>
        Get a desktop notification when someone messages you, even when this tab is in the
        background.
      </span>
      <button
        type="button"
        className="btn btn--primary btn--sm"
        disabled={requesting}
        onClick={() => void enable()}
      >
        {requesting ? 'Waiting…' : 'Turn on notifications'}
      </button>
      <button type="button" className="btn btn--ghost btn--sm" onClick={dismissPrompt}>
        Not now
      </button>
    </div>
  );
}
